// HUB-UI: customer-facing chrome shared by personal pages and the organization workspace.
// Menus use the active authorization snapshot, never a hard-coded admin flag.
import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Provider, useStore } from 'react-redux';
import { useTranslation } from 'react-i18next';
import type { SvgIconComponent } from '@mui/icons-material';
import SpaceDashboardOutlined from '@mui/icons-material/SpaceDashboardOutlined';
import EventNoteOutlined from '@mui/icons-material/EventNoteOutlined';
import TimelineOutlined from '@mui/icons-material/TimelineOutlined';
import WorkOutline from '@mui/icons-material/WorkOutline';
import FavoriteBorder from '@mui/icons-material/FavoriteBorder';
import PlaceOutlined from '@mui/icons-material/PlaceOutlined';
import NotificationsNoneOutlined from '@mui/icons-material/NotificationsNoneOutlined';
import ShieldOutlined from '@mui/icons-material/ShieldOutlined';
import LockOutlined from '@mui/icons-material/LockOutlined';
import StorefrontOutlined from '@mui/icons-material/StorefrontOutlined';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import TravelExploreOutlined from '@mui/icons-material/TravelExploreOutlined';
import GroupOutlined from '@mui/icons-material/GroupOutlined';
import AdminPanelSettingsOutlined from '@mui/icons-material/AdminPanelSettingsOutlined';
import TuneOutlined from '@mui/icons-material/TuneOutlined';
import CategoryOutlined from '@mui/icons-material/CategoryOutlined';
import RuleOutlined from '@mui/icons-material/RuleOutlined';
import CurrencyExchangeOutlined from '@mui/icons-material/CurrencyExchangeOutlined';
import ExtensionOutlined from '@mui/icons-material/ExtensionOutlined';
import HistoryOutlined from '@mui/icons-material/HistoryOutlined';
import ApartmentOutlined from '@mui/icons-material/ApartmentOutlined';
import PersonOutline from '@mui/icons-material/PersonOutline';
import FolderOpenOutlined from '@mui/icons-material/FolderOpenOutlined';
import SwapHorizOutlined from '@mui/icons-material/SwapHorizOutlined';
import LogoutOutlined from '@mui/icons-material/LogoutOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import FactCheckOutlined from '@mui/icons-material/FactCheckOutlined';
import BusinessCenterOutlined from '@mui/icons-material/BusinessCenterOutlined';
import CampaignOutlined from '@mui/icons-material/CampaignOutlined';
import { ROUTES } from '../../app/routes';
import { usePermissions } from '../../core/authorization/PermissionProvider';
import { store, useAppDispatch, useAppSelector } from '../../app/store';
import { signOut } from '../../app/sessionSlice';
import { usePageMeta } from '../../core/usePageMeta';
import { PublicLayout } from '../public/components';
import './hub.css';
import '../../main/theme/theme.css';

export const PERSONAL_MENU = [
  ['/me', 'Overview'],
  ['/me/activity', 'Reservations & orders'],
  ['/me/timeline', 'Timeline'],
  ['/me/applications', 'Applications'],
  ['/me/favourites', 'Favourite companies'],
  ['/me/cart', 'Cart'],
  ['/me/addresses', 'Addresses'],
  ['/me/notifications', 'Notification settings'],
  ['/me/account', 'Account & security'],
  ['/me/account/sessions', 'Logins History'],
  ['/me/privacy', 'Privacy'],
] as const;

/** Icons for known destinations; catalog menus without a match get a neutral folder. */
const MENU_ICONS: Record<string, SvgIconComponent> = {
  '/me': SpaceDashboardOutlined,
  '/me/account/sessions': HistoryOutlined,
  '/me/activity': EventNoteOutlined,
  '/me/timeline': TimelineOutlined,
  '/me/applications': WorkOutline,
  '/me/favourites': FavoriteBorder,
  '/me/addresses': PlaceOutlined,
  '/me/notifications': NotificationsNoneOutlined,
  '/me/account': ShieldOutlined,
  '/me/privacy': LockOutlined,
  '/app': SpaceDashboardOutlined,
  '/app/organization-profiles': StorefrontOutlined,
  '/app/organization-profiles/listings': Inventory2Outlined,
  '/app/jobs': WorkOutline,
  '/app/directory': TravelExploreOutlined,
  '/activity': HistoryOutlined,
  '/profile/sessions': HistoryOutlined,
  '/organization': ApartmentOutlined,
  '/users': GroupOutlined,
  '/roles': AdminPanelSettingsOutlined,
  '/role-actions': TuneOutlined,
  '/notifications': NotificationsNoneOutlined,
  '/notification-categories': CategoryOutlined,
  '/notification-user-categories': RuleOutlined,
  '/currencies': CurrencyExchangeOutlined,
  '/integration': ExtensionOutlined,
  '/profile': PersonOutline,
  '/app/companies': BusinessCenterOutlined,
  '/app/billing': ReceiptLongOutlined,
  '/app/billing/requests': FactCheckOutlined,
  [ROUTES.promotions]: CampaignOutlined,
};

/** Catalog titles such as `RoleActions` read as words ("Role actions") in the menu. */
function humanize(label: string) {
  if (/\s/.test(label)) return label;
  const words = label.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  return words.charAt(0).toUpperCase() + words.slice(1).replace(/ ([A-Z])(?=[a-z])/g, (_, c: string) => ` ${c.toLowerCase()}`);
}

function initials(first: string, last: string) {
  return `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase() || '·';
}

export function HubFrame({ children }: { children: ReactNode }) {
  const { hasScope } = usePermissions();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { organization, profile, snapshot, activeOrganizationId } = useAppSelector((state) => state.session);
  const routePersonal = pathname === '/me' || pathname.startsWith('/me/');
  const [menuArea, setMenuArea] = useState<boolean | null>(null);
  const personal = menuArea ?? routePersonal;
  useEffect(() => setMenuArea(null), [pathname]);
  const [busy, setBusy] = useState(false);
  const menus = snapshot.resources.filter(
    (resource) =>
      resource.kind === 'Menu' &&
      !resource.isHidden &&
      (!resource.isSuperAdminOnly || snapshot.isSuperAdmin === true) &&
      (resource.url !== '/app/organization-requests' || snapshot.isSuperAdmin === true) &&
      resource.canRead &&
      resource.url !== '/organization/memberships' &&
      resource.url !== '/feed' &&
      resource.url !== '/app/feed' &&
      resource.url?.startsWith('/') &&
      !resource.url.startsWith('//'),
  );
  const workspace = Array.from(
    new Map(
      menus.map((resource) => [resource.url!, { url: resource.url!, label: humanize(resource.label || resource.title) }]),
    ).values(),
  ).filter((item) => item.url !== '/app' && item.url !== '/profile/sessions');
  // Promotions use the same company-management permission as the billing APIs.
  if (hasScope('platform.organization.manage') && !workspace.some(item => item.url === ROUTES.promotions))
    workspace.push({ url: ROUTES.promotions, label: t('promotions.pageTitle', 'Promotions') });
  // Billing and organization requests come from the resource catalog.
  const extra: { url: string; label: string }[] = [
    { url: '/app/companies', label: 'My businesses' },
    { url: '/profile/sessions', label: 'Logins History' },
  ];
  const items: { url: string; label: string }[] = personal
    ? PERSONAL_MENU.map(([url, label]) => ({ url, label }))
    : [{ url: '/app', label: 'Dashboard' }, ...extra, ...workspace];

  // Longest matching prefix names the current page (e.g. /app/jobs/:id/applicants → Job postings).
  const current = items
    .filter((item) => pathname === item.url || pathname.startsWith(`${item.url}/`))
    .sort((a, b) => b.url.length - a.url.length)[0];
  const pageItems = routePersonal ? PERSONAL_MENU.map(([url, label]) => ({ url, label })) : [{ url: '/app', label: 'Dashboard' }, ...extra, ...workspace];
  const page = pageItems.filter((item) => pathname === item.url || pathname.startsWith(`${item.url}/`)).sort((a, b) => b.url.length - a.url.length)[0];
  usePageMeta(page ? t(`hub.menu.${page.url}`, page.label) : routePersonal ? 'My account' : 'Workspace', { noindex: true });

  const context = personal ? t('hub.member.personal', 'Personal') : organization.name;

  const groupFor = (url: string) => {
    if (personal) return ['/me/addresses', '/me/notifications', '/me/account', '/me/privacy'].includes(url) ? 'settings' : 'activity';
    if (url === '/activity' || url === '/profile/sessions') return 'activity';
    if (['/users', '/roles', '/role-actions', '/currencies', '/integration', '/profile'].some((path) => url === path || url.startsWith(path + '/'))) return 'settings';
    if (url.startsWith('/notification')) return 'notifications';
    if (url === '/app/billing' || url.startsWith('/app/billing/')) return 'billing';
    if (url === '/app' || url === '/app/companies' || url.startsWith('/organization')) return 'business';
    return 'workspace';
  };
  const labels: Record<string, string> = { business: 'Business', workspace: 'Workspace', billing: 'Billing & plans', notifications: 'Notifications', activity: 'Activity', settings: 'Settings' };
  const groups = Object.entries(labels).map(([key, label]) => ({ key, label: t('hub.member.groups.' + key, label), items: items.filter((item) => groupFor(item.url) === key) }));

  const navigation = (
        <aside className="hub-member-nav">
          <div className="hub-member-identity">
            <span className="hub-member-avatar" aria-hidden>
              {initials(profile.firstName, profile.lastName)}
            </span>
            <div className="hub-member-who">
              <strong>
                {profile.firstName} {profile.lastName}
              </strong>
              <span>{context}</span>
            </div>
          </div>
          {activeOrganizationId && (
            <div className="hub-area-switch" role="group" aria-label={t('hub.member.area', 'Area')} data-on={personal ? 'personal' : 'business'}>
              <span className="hub-area-thumb" aria-hidden />
              <button type="button" className={personal ? 'on' : undefined} aria-pressed={personal} onClick={() => setMenuArea(true)}>
                <PersonOutline aria-hidden sx={{ fontSize: 17 }} />
                {t('hub.member.personalTab', 'Personal')}
              </button>
              <button type="button" className={personal ? undefined : 'on'} aria-pressed={!personal} onClick={() => setMenuArea(false)}>
                <BusinessCenterOutlined aria-hidden sx={{ fontSize: 17 }} />
                {t('hub.member.businessTab', 'Business')}
              </button>
            </div>
          )}
          <nav aria-label={personal ? t('hub.member.accountNav', 'My account') : t('hub.member.workspaceNav', 'Workspace')}>
            {groups
              .filter((group) => group.items.length > 0)
              .map((group) => (
                <details key={group.key} className="hub-nav-group" open={!personal && group.items.some((item) => item.url === current?.url)}>
                  <summary className="hub-nav-label">{group.label}</summary>
                  <div className="hub-nav-group-items">
                  {group.items.map((item) => {
                    const Icon = MENU_ICONS[item.url] ?? FolderOpenOutlined;
                    return (
                      <NavLink key={item.url} to={item.url} end={item.url === '/me' || item.url === '/app' || item.url === '/me/account' || item.url === ROUTES.billing}>
                        <span className="hub-nav-icon" aria-hidden>
                          <Icon sx={{ fontSize: 19 }} />
                        </span>
                        <span>{t(`hub.menu.${item.url}`, item.label)}</span>
                      </NavLink>
                    );
                  })}
                  </div>
                </details>
              ))}
          </nav>
          <div className="hub-member-tools">
            <NavLink to="/switch" title={t('hub.member.switch', 'Switch company or app')}>
              <SwapHorizOutlined aria-hidden sx={{ fontSize: 19 }} />
              <span>{t('hub.member.switch', 'Switch company or app')}</span>
            </NavLink>
            <button
              type="button"
              title={t('hub.member.signOut', 'Sign out')}
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await dispatch(signOut()).unwrap();
                } finally {
                  setBusy(false);
                }
              }}
            >
              <LogoutOutlined aria-hidden sx={{ fontSize: 19 }} />
              <span>{busy ? t('hub.member.signingOut', 'Signing out…') : t('hub.member.signOut', 'Sign out')}</span>
            </button>
          </div>
        </aside>
  );

  return (
    <PublicLayout showHeaderSearch={false} applicationNavigation={navigation}>
      {/* Colors follow the app theme on <html>; no theme class is forced here. */}
      <div className="hub-wrap hub-member" data-area={personal ? 'personal' : 'business'}>
        <div className="hub-desktop-navigation">{navigation}</div>
        <div className="hub-member-content">{children}</div>
      </div>
    </PublicLayout>
  );
}
export function HubLayout() {
  return (
    <HubFrame>
      <Outlet />
    </HubFrame>
  );
}
// Legacy pages need their own Redux store for content; only chrome receives the Platform store.
export function LegacyHubFrame({ children }: { children: ReactNode }) {
  const contentStore = useStore();
  return (
    <Provider store={store}>
      <HubFrame>
        <Provider store={contentStore}>{children}</Provider>
      </HubFrame>
    </Provider>
  );
}
